import { PESTS_COVERED_STATUS } from '@ps/common-features/constants/pests'

/** Which portal is asking. Picks the column, not the row. */
export const PEST_PORTAL = {
  PROPERTY_MANAGER: 'propertyManager',
  RESIDENT: 'resident',
} as const

export type PestPortal = (typeof PEST_PORTAL)[keyof typeof PEST_PORTAL]

/** How emphatic a pest status is, for the portal to style from. */
export type PestTone = 'covered' | 'excluded' | 'paid' | 'pending' | 'warranty'

/** What the property manager portal shows. It has no i18n, so the wording lives here. */
export type PmPestText = { label: string; tone: PestTone }

/**
 * A key under `treatment.status` in the resident portal's locale files, so that portal
 * stays translatable. These are only the keys a row can produce; the resident app has
 * statuses of its own for a voided request and for a pest it does not show.
 */
export type ResidentPestKey =
  | 'approved'
  | 'covered'
  | 'declined'
  | 'needsReview'
  | 'notApproved'
  | 'notCovered'
  | 'paid'
  | 'paymentRequired'
  | 'propertyManagerReview'
  | 'quoteAtTimeOfService'

/** What the resident portal shows: the wording's key, and the tone to style it from. */
export type ResidentPestText = { key: ResidentPestKey; tone: PestTone }

/** One row of product's mapping sheet: the same situation, told to each portal. */
export type PestStatusText = {
  /** `null` when the portal shows nothing at all. */
  propertyManager: PmPestText | null
  resident: ResidentPestText | null
}

type PestCoverageStatus = (typeof PESTS_COVERED_STATUS)[keyof typeof PESTS_COVERED_STATUS]

/** The only pest statuses the API sets on a work order. */
const WORK_ORDER_PEST_STATUSES = [
  PESTS_COVERED_STATUS.COVERED,
  PESTS_COVERED_STATUS.WARRANTY,
  PESTS_COVERED_STATUS.QUOTE_TENANT,
  PESTS_COVERED_STATUS.QUOTE_PESTSHARE,
] as const

type WorkOrderPestStatus = (typeof WORK_ORDER_PEST_STATUSES)[number]

/**
 * Product's pest status mapping, one entry per row of the sheet.
 *
 * The row is chosen the same way for both portals — by the pest's status and whether it
 * came from the request or a work order. Only the column differs.
 */
export const PEST_STATUS_TEXT = {
  request: {
    [PESTS_COVERED_STATUS.COVERED]: {
      propertyManager: { label: 'Covered', tone: 'covered' },
      resident: { key: 'covered', tone: 'covered' },
    },
    [PESTS_COVERED_STATUS.EXPIRED_PM]: {
      propertyManager: { label: 'Auto-declined', tone: 'excluded' },
      resident: { key: 'notApproved', tone: 'excluded' },
    },
    [PESTS_COVERED_STATUS.EXPIRED_TENANT]: {
      propertyManager: { label: 'Auto-declined (resident)', tone: 'excluded' },
      resident: { key: 'declined', tone: 'excluded' },
    },
    [PESTS_COVERED_STATUS.NEEDS_ADDITIONAL_INFO]: {
      propertyManager: { label: 'Needs review', tone: 'pending' },
      resident: { key: 'propertyManagerReview', tone: 'pending' },
    },
    [PESTS_COVERED_STATUS.NEEDS_INFO_TENANT]: {
      propertyManager: { label: 'Resident payment required', tone: 'pending' },
      resident: { key: 'paymentRequired', tone: 'pending' },
    },
    [PESTS_COVERED_STATUS.NOT_COVERED]: {
      propertyManager: { label: 'Not covered', tone: 'excluded' },
      resident: { key: 'notCovered', tone: 'excluded' },
    },
    [PESTS_COVERED_STATUS.PAID_PM]: {
      propertyManager: { label: 'Approved', tone: 'paid' },
      resident: { key: 'approved', tone: 'paid' },
    },
    [PESTS_COVERED_STATUS.PAID_TENANT]: {
      propertyManager: { label: 'Paid by resident', tone: 'paid' },
      resident: { key: 'paid', tone: 'paid' },
    },
    [PESTS_COVERED_STATUS.QUOTE_PESTSHARE]: { propertyManager: null, resident: null },
    [PESTS_COVERED_STATUS.QUOTE_TENANT]: { propertyManager: null, resident: null },
    /**
     * The one row where the portals disagree on more than wording: the property manager
     * is told the pest is out of scope, while the resident is told it is still being
     * looked at. The tones follow those two messages, so they differ on purpose.
     */
    [PESTS_COVERED_STATUS.RED_BUCKET]: {
      propertyManager: { label: 'Not covered', tone: 'excluded' },
      resident: { key: 'needsReview', tone: 'pending' },
    },
    [PESTS_COVERED_STATUS.REJECTED_BY_TENANT]: {
      propertyManager: { label: 'Declined by resident', tone: 'excluded' },
      resident: { key: 'declined', tone: 'excluded' },
    },
    [PESTS_COVERED_STATUS.REJECTED_PM]: {
      propertyManager: { label: 'Not approved', tone: 'excluded' },
      resident: { key: 'notApproved', tone: 'excluded' },
    },
    [PESTS_COVERED_STATUS.WARRANTY]: {
      propertyManager: { label: 'Warranty', tone: 'warranty' },
      resident: { key: 'needsReview', tone: 'warranty' },
    },
  },
  workOrder: {
    [PESTS_COVERED_STATUS.COVERED]: {
      propertyManager: { label: 'Covered', tone: 'covered' },
      resident: { key: 'covered', tone: 'covered' },
    },
    [PESTS_COVERED_STATUS.QUOTE_PESTSHARE]: {
      propertyManager: { label: 'Quote to property manager', tone: 'pending' },
      resident: { key: 'needsReview', tone: 'pending' },
    },
    [PESTS_COVERED_STATUS.QUOTE_TENANT]: {
      propertyManager: { label: 'Quote to resident', tone: 'pending' },
      resident: { key: 'quoteAtTimeOfService', tone: 'pending' },
    },
    /**
     * Once a work order is under warranty the resident is simply told it is covered, so
     * their tone is `covered` where the property manager still sees the warranty badge.
     */
    [PESTS_COVERED_STATUS.WARRANTY]: {
      propertyManager: { label: 'Warranty', tone: 'warranty' },
      resident: { key: 'covered', tone: 'covered' },
    },
  },
  /** A covered work order keeps whoever paid on the request. */
  paidByPropertyManager: {
    propertyManager: { label: 'Approved', tone: 'paid' },
    resident: { key: 'approved', tone: 'paid' },
  },
  paidByResident: {
    propertyManager: { label: 'Paid by resident', tone: 'paid' },
    resident: { key: 'paid', tone: 'paid' },
  },
} satisfies {
  paidByPropertyManager: PestStatusText
  paidByResident: PestStatusText
  request: Record<PestCoverageStatus, PestStatusText>
  workOrder: Record<WorkOrderPestStatus, PestStatusText>
}

const isWorkOrderStatus = (status?: string): status is WorkOrderPestStatus =>
  WORK_ORDER_PEST_STATUSES.some((workOrderStatus) => workOrderStatus === status)

/**
 * Picks the row: which of a pest's two statuses to describe.
 *
 * A work order status wins over the request status, except that a covered work order
 * keeps whoever paid on the request. Statuses the API never sets on a work order fall
 * back to the request.
 */
const pickRow = (requestStatus: string, workOrderStatus?: string) => {
  if (isWorkOrderStatus(workOrderStatus)) {
    if (workOrderStatus === PESTS_COVERED_STATUS.COVERED) {
      if (requestStatus === PESTS_COVERED_STATUS.PAID_TENANT) {
        return PEST_STATUS_TEXT.paidByResident
      }

      if (requestStatus === PESTS_COVERED_STATUS.PAID_PM) {
        return PEST_STATUS_TEXT.paidByPropertyManager
      }
    }

    return PEST_STATUS_TEXT.workOrder[workOrderStatus]
  }

  return requestStatus in PEST_STATUS_TEXT.request
    ? PEST_STATUS_TEXT.request[requestStatus as PestCoverageStatus]
    : undefined
}

/**
 * Says what `portal` shows for a pest, from its status on the request and, when there is
 * one, on the work order.
 *
 * Returns `undefined` only for a request status outside `PESTS_COVERED_STATUS`, which
 * callers should treat as bad data and report.
 */
export const resolvePestStatusText = <P extends PestPortal>(
  portal: P,
  requestStatus: string,
  workOrderStatus?: string,
): PestStatusText[P] | undefined => pickRow(requestStatus, workOrderStatus)?.[portal]
